import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Hrempdash.css';

// Inject additional styles for history modal
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = `
    .hr-emp-dash-modal-xlarge {
      width: 95%;
      max-width: 1200px;
      max-height: 90vh;
      overflow-y: auto;
    }

    .history-status-approved {
      padding: 4px 12px;
      background-color: #e8f5e9;
      color: #2e7d32;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      display: inline-block;
    }

    .history-status-rejected {
      padding: 4px 12px;
      background-color: #ffebee;
      color: #c62828;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      display: inline-block;
    }

    .history-status-pending {
      padding: 4px 12px;
      background-color: #fff3e0;
      color: #f57c00;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      display: inline-block;
    }

    .history-tab-button {
      padding: 8px 16px;
      background-color: transparent;
      color: #666;
      border: 1px solid #ddd;
      border-radius: 6px;
      cursor: pointer;
      fontSize: 14px;
      font-weight: 500;
      transition: all 0.2s;
    }

    .history-tab-button.active {
      background-color: #1976d2;
      color: white;
      border: none;
    }

    .history-tab-button:hover {
      background-color: #e3f2fd;
    }

    .history-tab-button.active:hover {
      background-color: #1565c0;
    }
  `;
  document.head.appendChild(styleElement);
}

// Export Options Modal Component
const ExportOptionsModal = ({ employee, onClose, onExport }) => {
  const [exportType, setExportType] = useState('all'); // 'all', 'month', 'year'
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const getBackendUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (process.env.NODE_ENV === 'production') return 'https://api.leave-portal.manurevasolutions.com';
    return 'http://localhost:5000';
  };

  // Fetch employee's leave records on mount
  useEffect(() => {
    const fetchEmployeeLeaveData = async () => {
      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/employee/${employee.id}`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          setLeaveRecords(data.recent_requests || []);
          console.log('✅ Fetched employee leave records:', data.recent_requests?.length || 0);
        }
      } catch (error) {
        console.error('❌ Error fetching leave records:', error);
      } finally {
        setLoading(false);
      }
    };

    if (employee?.id) {
      fetchEmployeeLeaveData();
    }
  }, [employee?.id]);

  // Get available months (only months with data)
  const getAvailableMonths = () => {
    const monthsWithData = new Set();
    
    leaveRecords.forEach(record => {
      try {
        const dateParts = record.dateApplied.split('/');
        if (dateParts.length === 3) {
          const month = dateParts[1];
          const year = dateParts[2];
          const monthYear = `${year}-${month.padStart(2, '0')}`;
          monthsWithData.add(monthYear);
        }
      } catch (error) {
        console.error('Error parsing date:', record.dateApplied);
      }
    });

    // Convert to array and sort (newest first)
    const sortedMonths = Array.from(monthsWithData).sort((a, b) => b.localeCompare(a));
    
    // Format for display
    return sortedMonths.map(monthYear => {
      const date = new Date(monthYear + '-01');
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      return { value: monthYear, label };
    });
  };

  // Get available years (only years with data)
  const getAvailableYears = () => {
    const yearsWithData = new Set();
    
    leaveRecords.forEach(record => {
      try {
        const dateParts = record.dateApplied.split('/');
        if (dateParts.length === 3) {
          const year = dateParts[2];
          yearsWithData.add(year);
        }
      } catch (error) {
        console.error('Error parsing date:', record.dateApplied);
      }
    });

    // Convert to array and sort (newest first)
    return Array.from(yearsWithData).sort((a, b) => b.localeCompare(a));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExport(exportType, selectedMonth, selectedYear);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const availableMonths = getAvailableMonths();
  const availableYears = getAvailableYears();

  // Get record count for selected period
  const getRecordCount = () => {
    if (exportType === 'all') {
      return leaveRecords.length;
    // } else if (exportType === 'month' && selectedMonth) {
    //   return leaveRecords.filter(record => {
    //     try {
    //       const dateParts = record.dateApplied.split('/');
    //       if (dateParts.length === 3) {
    //         const recordMonth = `${dateParts[2]}-${dateParts[1].padStart(2, '0')}`;
    //         return recordMonth === selectedMonth;
    //       }
    //     } catch (error) {
    //       return false;
    //     }
    //     return false;
    //   }).length;
    } else if (exportType === 'year' && selectedYear) {
      return leaveRecords.filter(record => {
        try {
          const dateParts = record.dateApplied.split('/');
          if (dateParts.length === 3) {
            return dateParts[2] === selectedYear;
          }
        } catch (error) {
          return false;
        }
        return false;
      }).length;
    }
    return 0;
  };

  const recordCount = getRecordCount();

  return (
    <div className="hr-emp-dash-modal-overlay" onClick={onClose}>
      <div 
        className="hr-emp-dash-modal" 
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        <div className="hr-emp-dash-modal-header">
          <h2 className="hr-emp-dash-modal-title">Export Employee Report</h2>
          <button className="hr-emp-dash-modal-close" onClick={onClose} disabled={isExporting}>✕</button>
        </div>
        
        <div className="hr-emp-dash-modal-content">
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#e3f2fd', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '4px' }}>
                Employee: {employee?.name}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {employee?.email}
              </div>
              {loading ? (
                <div style={{ fontSize: '13px', color: '#1976d2', marginTop: '4px' }}>
                  ⏳ Loading leave records...
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#1976d2', marginTop: '4px' }}>
                  📊 Total Records: {leaveRecords.length}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Export Type *
              </label>
              <select
                value={exportType}
                onChange={(e) => {
                  setExportType(e.target.value);
                  setSelectedMonth('');
                  setSelectedYear('');
                }}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <option value="all">📊 Complete History (All Time)</option>
                <option value="month" disabled={availableMonths.length === 0}>
                  📅 Month-wise Data {availableMonths.length === 0 ? '(No data)' : ''}
                </option>
                <option value="year" disabled={availableYears.length === 0}>
                  📆 Year-wise Data {availableYears.length === 0 ? '(No data)' : ''}
                </option>
              </select>
            </div>

            {exportType === 'month' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Select Month * <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                    (Only months with leave records)
                  </span>
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={loading || availableMonths.length === 0}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: loading || availableMonths.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: loading || availableMonths.length === 0 ? 0.6 : 1
                  }}
                >
                  <option value="">-- Select Month --</option>
                  {availableMonths.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                {availableMonths.length === 0 && !loading && (
                  <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                    ⚠️ No leave records found for any month
                  </div>
                )}
              </div>
            )}

            {exportType === 'year' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Select Year * <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                    (Only years with leave records)
                  </span>
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={loading || availableYears.length === 0}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: loading || availableYears.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: loading || availableYears.length === 0 ? 0.6 : 1
                  }}
                >
                  <option value="">-- Select Year --</option>
                  {availableYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                {availableYears.length === 0 && !loading && (
                  <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                    ⚠️ No leave records found for any year
                  </div>
                )}
              </div>
            )}

            {recordCount > 0 && (
              <div style={{
                padding: '12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#0369a1',
                marginBottom: '16px',
                border: '1px solid #bae6fd'
              }}>
                <strong>📊 Records to export:</strong> {recordCount} leave request{recordCount !== 1 ? 's' : ''}
              </div>
            )}
{/* 
            <div style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#666',
              marginTop: '16px'
            }}>
              <strong>📄 Export includes:</strong>
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                <li>Personal information</li>
                <li>Leave balance details</li>
                <li>Leave history with dates and status</li>
                <li>Statistics and utilization</li>
              </ul>
            </div> */}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              onClick={handleExport}
              disabled={
                loading ||
                isExporting || 
                (exportType === 'month' && !selectedMonth) ||
                (exportType === 'year' && !selectedYear) ||
                recordCount === 0
              }
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: 
                  loading || recordCount === 0 || (exportType === 'month' && !selectedMonth) || (exportType === 'year' && !selectedYear) 
                  ? '#ccc' 
                  : '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 
                  loading || recordCount === 0 || (exportType === 'month' && !selectedMonth) || (exportType === 'year' && !selectedYear) || isExporting 
                  ? 'not-allowed' 
                  : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isExporting ? (
                <>⏳ Exporting...</>
              ) : loading ? (
                <>⏳ Loading...</>
              ) : recordCount === 0 && exportType !== 'all' ? (
                <>⚠️ No Data</>
              ) : (
                <>📥 Export CSV</>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isExporting}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: 'white',
                color: '#666',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isExporting ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
const FullHistoryModal = ({ employee, onClose }) => {
  const [historyData, setHistoryData] = useState({
    casual: [],
    medical: [],
    permission: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLeaveType, setFilterLeaveType] = useState('all');

  const getBackendUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (process.env.NODE_ENV === 'production') return 'https://api.leave-portal.manurevasolutions.com';
    return 'http://localhost:5000';
  };

  useEffect(() => {
    if (employee?.id) {
      fetchFullHistory();
    }
  }, [employee?.id]);

  const fetchFullHistory = async () => {
    setIsLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/employee/${employee.id}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Full history data:', data);
        
        // Categorize requests by leave type
        const categorized = {
          casual: [],
          medical: [],
          permission: []
        };

        if (data.recent_requests) {
          data.recent_requests.forEach(req => {
            const leaveType = req.leaveType || req.leave_type || '';
            
            if (leaveType.toLowerCase().includes('casual')) {
              categorized.casual.push(req);
            } else if (leaveType.toLowerCase().includes('medical') || leaveType.toLowerCase().includes('sick')) {
              categorized.medical.push(req);
            } else if (leaveType.toLowerCase().includes('permission') || leaveType.toLowerCase().includes('emergency')) {
              categorized.permission.push(req);
            }
          });
        }

        console.log('📊 Categorized:', {
          casual: categorized.casual.length,
          medical: categorized.medical.length,
          permission: categorized.permission.length
        });

        setHistoryData(categorized);
      }
    } catch (error) {
      console.error('❌ Error fetching history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'approved') return 'history-status-approved';
    if (statusLower === 'rejected') return 'history-status-rejected';
    if (statusLower === 'pending') return 'history-status-pending';
    return 'history-status-pending';
  };

  const getAllRequests = () => {
    return [...historyData.casual, ...historyData.medical, ...historyData.permission]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const getFilteredRequests = () => {
    let requests = getAllRequests(); // Always start with all requests

    // Filter by status
    if (filterStatus !== 'all') {
      requests = requests.filter(req => 
        req.status.toLowerCase() === filterStatus.toLowerCase()
      );
    }

    // Filter by leave type (from dropdown)
    if (filterLeaveType !== 'all') {
      requests = requests.filter(req => {
        const leaveType = (req.leaveType || req.leave_type || '').toLowerCase();
        
        if (filterLeaveType === 'casual') {
          return leaveType.includes('casual');
        } else if (filterLeaveType === 'medical') {
          return leaveType.includes('medical') || leaveType.includes('sick');
        } else if (filterLeaveType === 'permission') {
          return leaveType.includes('permission') || leaveType.includes('emergency');
        }
        return true;
      });
    }

    return requests;
  };

  const calculateStats = (requests) => {
    const approved = requests.filter(r => r.status.toLowerCase() === 'approved');
    const totalDays = approved.reduce((sum, r) => sum + (r.daysRequested || 1), 0);
    return {
      total: requests.length,
      approved: approved.length,
      pending: requests.filter(r => r.status.toLowerCase() === 'pending').length,
      rejected: requests.filter(r => r.status.toLowerCase() === 'rejected').length,
      totalDays
    };
  };

  const handleExport = () => {
    try {
      const requests = getFilteredRequests();
      const csvContent = [
        ['Date Applied', 'Type', 'From', 'To', 'Duration', 'Status', 'Reason', 'Comments'].join(','),
        ...requests.map(req => [
          req.dateApplied || '',
          req.leaveType || '',
          req.startDate || '',
          req.endDate || '',
          req.duration || '',
          req.status || '',
          `"${(req.reason || '').replace(/"/g, '""')}"`,
          `"${(req.managerComments || '').replace(/"/g, '""')}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${employee.name.replace(/\s/g, '_')}_leave_history_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      alert('✅ History exported successfully!');
    } catch (error) {
      console.error('❌ Export error:', error);
      alert('Failed to export history');
    }
  };

  const filteredRequests = getFilteredRequests();
  const stats = calculateStats(filteredRequests);

  return (
    <div className="hr-emp-dash-modal-overlay" onClick={onClose}>
      <div className="hr-emp-dash-modal hr-emp-dash-modal-xlarge" onClick={(e) => e.stopPropagation()}>
        <div className="hr-emp-dash-modal-header">
          <div>
            <h2 className="hr-emp-dash-modal-title">Full Leave History</h2>
            <p style={{ fontSize: '14px', color: '#666', margin: '4px 0 0 0' }}>
              {employee?.name} - Complete leave record
            </p>
          </div>
          <button className="hr-emp-dash-modal-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="hr-emp-dash-modal-content">
          {/* Stats Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
            <div style={{ padding: '16px', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
              <div style={{ fontSize: '12px', color: '#1976d2', fontWeight: '600', marginBottom: '8px' }}>Total Requests</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#1565c0' }}>{stats.total}</div>
              <div style={{ fontSize: '11px', color: '#42a5f5', marginTop: '4px' }}>All time</div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '1px solid #a5d6a7' }}>
              <div style={{ fontSize: '12px', color: '#388e3c', fontWeight: '600', marginBottom: '8px' }}>Approved</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#2e7d32' }}>{stats.approved}</div>
              <div style={{ fontSize: '11px', color: '#66bb6a', marginTop: '4px' }}>{stats.totalDays} days total</div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffcc80' }}>
              <div style={{ fontSize: '12px', color: '#f57c00', fontWeight: '600', marginBottom: '8px' }}>Pending</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef6c00' }}>{stats.pending}</div>
              <div style={{ fontSize: '11px', color: '#ffa726', marginTop: '4px' }}>Awaiting approval</div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#fce4ec', borderRadius: '8px', border: '1px solid #f48fb1' }}>
              <div style={{ fontSize: '12px', color: '#c2185b', fontWeight: '600', marginBottom: '8px' }}>Rejected</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ad1457' }}>{stats.rejected}</div>
              <div style={{ fontSize: '11px', color: '#ec407a', marginTop: '4px' }}>Declined requests</div>
            </div>
          </div>

          {/* Filters Only */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px', gap: '8px' }}>
            <select
              value={filterLeaveType}
              onChange={(e) => setFilterLeaveType(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: 'white',
                minWidth: '150px'
              }}
            >
              <option value="all">All Leave Types</option>
              <option value="casual">🌴 Casual</option>
              <option value="medical">🏥 Medical</option>
              <option value="permission">⏱️ Permission</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: 'white',
                minWidth: '150px'
              }}
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* History List */}
          <div style={{ maxHeight: '450px', overflowY: 'auto', paddingRight: '8px' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
                <div style={{ fontSize: '16px' }}>Loading history...</div>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
                <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>No leave requests found</div>
                <div style={{ fontSize: '14px', color: '#999' }}>Try adjusting your filters</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredRequests.map((request, index) => (
                  <div
                    key={`${request.id || index}-${index}`}
                    style={{
                      padding: '16px',
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      transition: 'all 0.2s',
                      cursor: 'default'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = '#1976d2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: '6px 14px',
                            backgroundColor: request.leaveType?.toLowerCase().includes('casual') ? '#e3f2fd' :
                                           request.leaveType?.toLowerCase().includes('medical') ? '#ffebee' : '#fff3e0',
                            color: request.leaveType?.toLowerCase().includes('casual') ? '#1976d2' :
                                   request.leaveType?.toLowerCase().includes('medical') ? '#c62828' : '#f57c00',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600'
                          }}>
                            {request.leaveType || 'Leave'}
                          </span>
                          <span className={getStatusBadgeClass(request.status)}>
                            {request.status}
                          </span>
                          <span style={{ fontSize: '13px', color: '#999', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            📅 {request.dateApplied}
                          </span>
                        </div>
                        
                        <div style={{ fontSize: '14px', color: '#333', marginBottom: '10px', lineHeight: '1.5' }}>
                          <strong style={{ color: '#1976d2' }}>Period:</strong> {request.dateRange} 
                          <span style={{ 
                            marginLeft: '12px', 
                            padding: '2px 8px', 
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            color: '#666',
                            fontSize: '13px'
                          }}>
                            {request.duration}
                          </span>
                        </div>
                        
                        <div style={{ 
                          fontSize: '13px', 
                          color: '#555', 
                          marginBottom: '8px',
                          padding: '10px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          borderLeft: '3px solid #90caf9'
                        }}>
                          <strong style={{ color: '#1976d2' }}>Reason:</strong> {request.reason || 'No reason provided'}
                        </div>
                        
                        {request.managerComments && request.managerComments !== 'No comments' && (
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#555',
                            padding: '10px',
                            backgroundColor: '#e8f5e9',
                            borderRadius: '6px',
                            marginTop: '8px',
                            borderLeft: '3px solid #66bb6a'
                          }}>
                            <strong style={{ color: '#2e7d32' }}>💬 Manager Comments:</strong> {request.managerComments}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px', 
            marginTop: '24px', 
            paddingTop: '16px', 
            borderTop: '2px solid #e0e0e0' 
          }}>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Showing {filteredRequests.length} of {getAllRequests().length} requests
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleExport}
                disabled={filteredRequests.length === 0}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#fff',
                  color: '#1976d2',
                  border: '2px solid #1976d2',
                  borderRadius: '6px',
                  cursor: filteredRequests.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: filteredRequests.length === 0 ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (filteredRequests.length > 0) {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                📥 Export History
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1565c0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1976d2';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// AdjustBalanceModal Component
const AdjustBalanceModal = ({ employee, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    leaveType: '',
    adjustmentType: '',
    numberOfDays: '',
    reason: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  useEffect(() => {
    if (employee?.id) {
      fetchEmployeeBalance();
    }
  }, [employee?.id]);

  const getBackendUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (process.env.NODE_ENV === 'production') return 'https://api.leave-portal.manurevasolutions.com';
    return 'http://localhost:5000';
  };

  const fetchEmployeeBalance = async () => {
    setIsLoadingBalance(true);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/employee/${employee.id}/leave-balance`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentBalance(data.balances);
        console.log('✅ Fetched employee balance:', data.balances);
      } else {
        console.error('Failed to fetch employee balance');
      }
    } catch (error) {
      console.error('Error fetching employee balance:', error);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.leaveType) {
      newErrors.leaveType = 'Please select a leave type';
    }

    if (!formData.adjustmentType) {
      newErrors.adjustmentType = 'Please select an adjustment type';
    }

    if (!formData.numberOfDays) {
      newErrors.numberOfDays = 'Please enter number of days';
    } else {
      const days = parseFloat(formData.numberOfDays);
      if (isNaN(days) || days <= 0) {
        newErrors.numberOfDays = 'Please enter a valid positive number';
      } else if (days > 365) {
        newErrors.numberOfDays = 'Number of days cannot exceed 365';
      }
    }

    if (!formData.reason || formData.reason.trim().length < 20) {
      newErrors.reason = 'Please provide a detailed reason (minimum 20 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const backendUrl = getBackendUrl();
      
      const adjustmentData = {
        employeeId: employee.id,
        leaveType: formData.leaveType,
        adjustmentType: formData.adjustmentType,
        numberOfDays: parseFloat(formData.numberOfDays),
        reason: formData.reason.trim()
      };

      console.log('🔧 Submitting adjustment:', adjustmentData);

      const response = await fetch(`${backendUrl}/api/adjust-leave-balance`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(adjustmentData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Adjustment successful:', result);
        
        alert(`Leave balance adjusted successfully!

${result.message}

Previous Balance: ${result.adjustment?.previous_balance?.allocated} days
New Balance: ${result.adjustment?.new_balance?.allocated} days`);
        
        if (onSubmit) {
          await onSubmit(adjustmentData);
        }
        
        onClose();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to adjust balance');
      }
      
    } catch (error) {
      console.error('❌ Adjustment error:', error);
      setErrors({ submit: error.message || 'Failed to adjust balance' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedBalance = () => {
    if (!currentBalance || !formData.leaveType) return null;
    
    const balanceMap = {
      'casual': currentBalance.casual,
      'medical': currentBalance.medical,
      'permission': currentBalance.permission
    };
    
    return balanceMap[formData.leaveType];
  };

  const selectedBalance = getSelectedBalance();

  return (
    <div className="hr-emp-dash-modal-overlay" onClick={onClose}>
      <div className="hr-emp-dash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hr-emp-dash-modal-header">
          <h2 className="hr-emp-dash-modal-title">Adjust Leave Balance</h2>
          <button 
            className="hr-emp-dash-modal-close" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>
        
        <div className="hr-emp-dash-modal-content">
          <div className="hr-emp-dash-employee-label">
            Employee: {employee?.name || 'Unknown'}
          </div>

          <div className="hr-emp-dash-alert">
            <span className="hr-emp-dash-alert-icon" style={{color:'#A78602'}}>⚠ </span>
            <div>
              <strong style={{color:'#A78602'}}>Important:</strong>
              <p>This adjustment will affect ONLY this employee's balance. Balance adjustments will be logged in the audit trail. Please provide a clear reason for the adjustment.</p>
            </div>
          </div>

          {isLoadingBalance ? (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '8px', 
              marginBottom: '16px',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              Loading current balance...
            </div>
          ) : currentBalance && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#e0f2fe', 
              border: '1px solid #0369a1',
              borderRadius: '8px', 
              marginBottom: '16px' 
            }}>
              <div style={{ fontWeight: '600', color: '#0c4a6e', marginBottom: '8px' }}>
                Current Leave Balances:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '14px' }}>
                <div>
                  <div style={{ color: '#075985', fontWeight: '500' }}>Casual Leave</div>
                  <div style={{ color: '#0369a1' }}>
                    {currentBalance.casual?.allocated || 0} allocated<br/>
                    {currentBalance.casual?.used || 0} used<br/>
                    <strong>{currentBalance.casual?.remaining || 0} remaining</strong>
                  </div>
                </div>
                <div>
                  <div style={{ color: '#075985', fontWeight: '500' }}>Medical Leave</div>
                  <div style={{ color: '#0369a1' }}>
                    {currentBalance.medical?.allocated || 0} allocated<br/>
                    {currentBalance.medical?.used || 0} used<br/>
                    <strong>{currentBalance.medical?.remaining || 0} remaining</strong>
                  </div>
                </div>
                <div>
                  <div style={{ color: '#075985', fontWeight: '500' }}>Permission</div>
                  <div style={{ color: '#0369a1' }}>
                    {currentBalance.permission?.allocated || 0} hours<br/>
                    {currentBalance.permission?.used || 0} used<br/>
                    <strong>{currentBalance.permission?.remaining || 0} remaining</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="hr-emp-dash-form-group">
            <label className="hr-emp-dash-label">Leave Type *</label>
            <select 
              className="hr-emp-dash-select"
              value={formData.leaveType}
              onChange={(e) => handleChange('leaveType', e.target.value)}
              disabled={isSubmitting}
              style={{ borderColor: errors.leaveType ? '#dc2626' : undefined }}
            >
              <option value="">Select leave type</option>
              <option value="casual">Casual Leave</option>
              <option value="medical">Medical Leave</option>
              <option value="permission">Permission (Hours)</option>
            </select>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
              ℹ️ Note: Casual Leave allocated days can be managed in Settings.
            </div>
            {errors.leaveType && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                {errors.leaveType}
              </div>
            )}
          </div>

          {selectedBalance && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fef3c7', 
              border: '1px solid #f59e0b',
              borderRadius: '6px', 
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                Current {formData.leaveType === 'casual' ? 'Casual Leave' : formData.leaveType === 'medical' ? 'Medical Leave' : 'Permission'} Balance:
              </div>
              <div style={{ color: '#78716c' }}>
                Allocated: {selectedBalance.allocated || 0} | 
                Used: {selectedBalance.used || 0} | 
                Remaining: {selectedBalance.remaining || 0}
              </div>
            </div>
          )}

          <div className="hr-emp-dash-form-group">
            <label className="hr-emp-dash-label">Adjustment Type *</label>
            <select 
              className="hr-emp-dash-select"
              value={formData.adjustmentType}
              onChange={(e) => handleChange('adjustmentType', e.target.value)}
              disabled={isSubmitting}
              style={{ borderColor: errors.adjustmentType ? '#dc2626' : undefined }}
            >
              <option value="">Select adjustment type</option>
              <option value="add">Add Days/Hours</option>
              <option value="deduct">Deduct Days/Hours</option>
              <option value="reset">Reset Balance</option>
            </select>
            {errors.adjustmentType && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                {errors.adjustmentType}
              </div>
            )}
            {formData.adjustmentType && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {formData.adjustmentType === 'add' && '➕ Will increase the allocated days/hours'}
                {formData.adjustmentType === 'deduct' && '➖ Will decrease the allocated days/hours'}
                {formData.adjustmentType === 'reset' && '🔄 Will set allocated days/hours to specified value and reset used count to 0'}
              </div>
            )}
          </div>

          <div className="hr-emp-dash-form-group">
            <label className="hr-emp-dash-label">
              Number of {formData.leaveType === 'permission' ? 'Hours' : 'Days'} *
            </label>
            <input
              type="number"
              className="hr-emp-dash-select"
              placeholder={`Enter number of ${formData.leaveType === 'permission' ? 'hours' : 'days'}`}
              value={formData.numberOfDays}
              onChange={(e) => handleChange('numberOfDays', e.target.value)}
              disabled={isSubmitting}
              min="0"
              step="0.5"
              style={{ 
                borderColor: errors.numberOfDays ? '#dc2626' : undefined,
                padding: '8px 12px'
              }}
            />
            {errors.numberOfDays && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                {errors.numberOfDays}
              </div>
            )}
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
              {formData.leaveType === 'permission' 
                ? 'Enter hours (e.g., 1, 1.5, 2)' 
                : 'Enter days (e.g., 1, 2, 5)'}
            </div>
            
            {formData.numberOfDays && selectedBalance && formData.adjustmentType && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px', 
                backgroundColor: '#f0fdf4',
                border: '1px solid #10b981',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#065f46'
              }}>
                <strong>Preview:</strong>
                {formData.adjustmentType === 'add' && (
                  <div>
                    Remaining: {selectedBalance.remaining} → 
                    New Remaining: {selectedBalance.remaining + parseFloat(formData.numberOfDays)}
                  </div>
                )}
                {formData.adjustmentType === 'deduct' && (
                  <div>
                    Remaining: {selectedBalance.remaining} → 
                    New Remaining: {Math.max(0, selectedBalance.remaining - parseFloat(formData.numberOfDays))}
                  </div>
                )}
                {formData.adjustmentType === 'reset' && (
                  <div>
                    Will reset to: {parseFloat(formData.numberOfDays)} (used count will be reset to 0, remaining will be {parseFloat(formData.numberOfDays)})
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="hr-emp-dash-form-group">
            <label className="hr-emp-dash-label">Reason for Adjustment *</label>
            <textarea 
              className="hr-emp-dash-textarea"
              placeholder="Please provide a detailed reason for adjustment (e.g., 'Bonus leave granted for exceptional performance', 'Correction due to system error', etc.)"
              rows="4"
              value={formData.reason}
              onChange={(e) => handleChange('reason', e.target.value)}
              disabled={isSubmitting}
              maxLength={500}
              style={{ borderColor: errors.reason ? '#dc2626' : undefined }}
            />
            {errors.reason && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                {errors.reason}
              </div>
            )}
            <div className="hr-emp-dash-char-count">
              {formData.reason.length}/500 characters 
              {formData.reason.length < 20 && ' (Minimum 20 characters required)'}
            </div>
          </div>

          {errors.submit && (
            <div style={{ 
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #dc2626',
              borderRadius: '6px',
              color: '#991b1b',
              marginBottom: '16px'
            }}>
              {errors.submit}
            </div>
          )}

          <div className="hr-emp-dash-modal-actions-btn-pop">
            <button 
              className="hr-emp-dash-btn-apply"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoadingBalance}
              style={{ 
                opacity: (isSubmitting || isLoadingBalance) ? 0.6 : 1,
                cursor: (isSubmitting || isLoadingBalance) ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Applying...' : 'Apply'}
            </button>
            <button 
              className="hr-emp-dash-btn-cancel" 
              onClick={onClose}
              disabled={isSubmitting}
              style={{ 
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const HREmployeeDashboard = () => {
  const location = useLocation();
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [showAdjustBalance, setShowAdjustBalance] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All Departments');
  const [statusFilter, setStatusFilter] = useState('All Status');

  const getBackendUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (process.env.NODE_ENV === 'production') return 'https://api.leave-portal.manurevasolutions.com';
    return 'http://localhost:5000';
  };

  const BACKEND_URL = getBackendUrl();

  const formatLastLeaveDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'No leaves yet';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;
      
      if (diffDays === 0) return `Today (${formattedDate})`;
      if (diffDays === 1) return `Yesterday (${formattedDate})`;
      if (diffDays < 7) return `${diffDays} days ago (${formattedDate})`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago (${formattedDate})`;
      
      return formattedDate;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/user`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          console.log('Current user loaded:', userData);
          setCurrentUser(userData);
        } else {
          console.error('Failed to fetch user data');
          setError('Failed to load user information');
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
        setError('Error loading user information');
      }
    };

    fetchCurrentUser();
  }, [BACKEND_URL]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/employee-list`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        const employeeRoleOnly = data.filter(emp => {
          const role = (emp.role || '').toString().toLowerCase();
          return role === 'employee';
        });
        
        console.log(`✅ Filtered ${employeeRoleOnly.length} employees from ${data.length} total users`);
        
        const employeesWithBalances = await Promise.all(
          employeeRoleOnly.map(async (emp) => {
            try {
              const balanceResponse = await fetch(
                `${BACKEND_URL}/api/employee/${emp.id}/leave-balance`,
                {
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' }
                }
              );

              let balanceData = null;
              if (balanceResponse.ok) {
                balanceData = await balanceResponse.json();
                console.log(`✅ Fetched balance for ${emp.name}:`, balanceData.balances);
              }

              const detailsResponse = await fetch(
                `${BACKEND_URL}/api/employee/${emp.id}`,
                {
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' }
                }
              );

              let lastLeaveDate = emp.last_activity_display || 'N/A';
              if (detailsResponse.ok) {
                const detailsData = await detailsResponse.json();
                if (detailsData.recent_requests && detailsData.recent_requests.length > 0) {
                  const approvedLeaves = detailsData.recent_requests.filter(
                    req => req.status === 'Approved'
                  );
                  if (approvedLeaves.length > 0) {
                    lastLeaveDate = approvedLeaves[0].start_date;
                  }
                }
              }
              
              const casualBalance = balanceData?.balances?.casual || { allocated: 15, used: 0, remaining: 15, carry_forward_from_previous_month: 0 };
              const medicalBalance = balanceData?.balances?.medical || { allocated: 15, used: 0, remaining: 15, carry_forward_days: 0 };
              const permissionBalance = balanceData?.balances?.permission || { allocated: 2, used: 0, remaining: 2 };
              
              return {
                ...emp,
                casual_allocated: casualBalance.allocated,
                casual_used: casualBalance.used,
                casual_remaining: casualBalance.remaining,
                casual_carry_forward: casualBalance.carry_forward_from_previous_month,
                medical_allocated: medicalBalance.allocated,
                medical_used: medicalBalance.used,
                medical_remaining: medicalBalance.remaining,
                medical_carry_forward: medicalBalance.carry_forward_days,
                permission_allocated: permissionBalance.allocated,
                permission_used: permissionBalance.used,
                permission_remaining: permissionBalance.remaining,
                last_leave_date: lastLeaveDate,
              };
            } catch (error) {
              console.error(`❌ Error fetching data for ${emp.name}:`, error);
              return {
                ...emp,
                casual_allocated: 15,
                casual_used: 0,
                casual_remaining: 15,
                casual_carry_forward: 0,
                medical_allocated: 15,
                medical_used: 0,
                medical_remaining: 15,
                medical_carry_forward: 0,
                permission_allocated: 2,
                permission_used: 0,
                permission_remaining: 2,
                last_leave_date: 'N/A'
              };
            }
          })
        );
        
        setEmployees(employeesWithBalances);
      } else {
        console.error('Failed to fetch employees');
        setError('Failed to load employees');
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Error loading employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [BACKEND_URL]);

  const handleLogout = async () => {
    try {
      console.log('Logging out...');
      
      const response = await fetch(`${BACKEND_URL}/auth/logout`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        console.log('Logout successful, redirecting...');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/';
      } else {
        console.error('Logout failed:', response.status);
        alert('Logout failed. Please try again.');
      }
    } catch (err) {
      console.error('Logout error:', err);
      alert('Error during logout. Please try again.');
    }
  };

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeDetails(true);
  };

  const handleAdjust = (employee) => {
    setSelectedEmployee(employee);
    setShowAdjustBalance(true);
  };

  const handleAdjustmentSubmit = async (adjustmentData) => {
    console.log('✅ Adjustment submitted successfully:', adjustmentData);
    
    await fetchEmployees();
    
    // ✅ DISPATCH EVENT: Notify all open windows/tabs of the balance change
    const event = new CustomEvent('leaveBalanceAdjusted', {
      detail: {
        employeeId: adjustmentData.employeeId,
        leaveType: adjustmentData.leaveType,
        adjustmentType: adjustmentData.adjustmentType,
        numberOfDays: adjustmentData.numberOfDays,
        timestamp: Date.now(),
        affectedUser: true  // ← Add this flag to identify it's a real change
      }
    });
    window.dispatchEvent(event);
    
    console.log('📢 Event dispatched: leaveBalanceAdjusted', event.detail);
  };

  const handleExportWithOptions = async (exportType, selectedMonth, selectedYear) => {
    try {
      const backendUrl = getBackendUrl();
      
      // Fetch employee's complete leave history
      const response = await fetch(`${backendUrl}/api/employee/${selectedEmployee.id}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch employee data');
      }

      const employeeData = await response.json();
      let recordsToExport = employeeData.recent_requests || [];

      // Filter records based on export type
      if (exportType === 'month' && selectedMonth) {
        recordsToExport = recordsToExport.filter(record => {
          try {
            const dateParts = record.dateApplied.split('/');
            if (dateParts.length === 3) {
              const recordMonth = `${dateParts[2]}-${dateParts[1].padStart(2, '0')}`;
              return recordMonth === selectedMonth;
            }
          } catch (error) {
            return false;
          }
          return false;
        });
      } else if (exportType === 'year' && selectedYear) {
        recordsToExport = recordsToExport.filter(record => {
          try {
            const dateParts = record.dateApplied.split('/');
            if (dateParts.length === 3) {
              return dateParts[2] === selectedYear;
            }
          } catch (error) {
            return false;
          }
          return false;
        });
      }

      if (recordsToExport.length === 0) {
        alert('No records found for the selected period');
        return;
      }

      // Prepare CSV export similar to Leave-history.jsx
      const headers = [
        'EMPLOYEE REPORT',
        '',
        `Generated on: ${new Date().toLocaleString()}`,
        '',
        'PERSONAL INFORMATION',
        `Name,${selectedEmployee.name}`,
        `Email,${selectedEmployee.email}`,
        `Role,${selectedEmployee.role}`,
        `Department,${selectedEmployee.department || selectedEmployee.role}`,
        `Manager,${selectedEmployee.manager_name || 'N/A'}`,
        `Phone,${selectedEmployee.ContactNumber1 || 'N/A'}`,
        `Address,${selectedEmployee.Address || 'N/A'}`,
        `Join Date,${selectedEmployee.joinDate || selectedEmployee.member_since || 'N/A'}`,
        '',
        'LEAVE BALANCE SUMMARY',
        'Leave Type,Used,Allocated,Remaining,Utilization %',
        `Casual Leave,${selectedEmployee.casual_used || 0},${selectedEmployee.casual_allocated || 15},${selectedEmployee.casual_remaining || 15},${Math.round(((selectedEmployee.casual_used || 0) / (selectedEmployee.casual_allocated || 15)) * 100)}%`,
        `Medical Leave,${selectedEmployee.medical_used || 0},${selectedEmployee.medical_allocated || 15},${selectedEmployee.medical_remaining || 15},${Math.round(((selectedEmployee.medical_used || 0) / (selectedEmployee.medical_allocated || 15)) * 100)}%`,
        `Permission,${selectedEmployee.permission_used || 0},${selectedEmployee.permission_allocated || 2},${selectedEmployee.permission_remaining || 2},${Math.round(((selectedEmployee.permission_used || 0) / (selectedEmployee.permission_allocated || 2)) * 100)}%`,
        '',
        // `LEAVE HISTORY - ${exportType === 'month' ? new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' }) : exportType === 'year' ? selectedYear : 'Complete History'}`,
        // 'Date Applied,Leave Type,Duration,Date Range,Status,Reason,Manager Comments',
        // ...recordsToExport.map(record => 
        //   `"${record.dateApplied}","${record.leaveType}","${record.duration}","${record.dateRange}","${record.status}","${(record.reason || '').replace(/"/g, '""')}","${(record.managerComments || '').replace(/"/g, '""')}"`
        // ),
        '',
        'STATISTICS',
        `Total Requests,${recordsToExport.length}`,
        `Approved,${recordsToExport.filter(r => r.status.toLowerCase() === 'approved').length}`,
        `Pending,${recordsToExport.filter(r => r.status.toLowerCase() === 'pending').length}`,
        `Rejected,${recordsToExport.filter(r => r.status.toLowerCase() === 'rejected').length}`,
        `Total Days Used,${recordsToExport.filter(r => r.status.toLowerCase() === 'approved').reduce((sum, r) => sum + (r.daysRequested || 1), 0)}`,
        `Overall Utilization,${Math.round(((selectedEmployee.casual_used || 0) + (selectedEmployee.medical_used || 0)) / ((selectedEmployee.casual_allocated || 15) + (selectedEmployee.medical_allocated || 15)) * 100)}%`,
        `Last Leave Date,${formatLastLeaveDate(selectedEmployee.last_leave_date)}`
      ].join('\n');

      const blob = new Blob([headers], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      let fileName = `${selectedEmployee.name.replace(/\s/g, '_')}_Leave_Report`;
      if (exportType === 'month' && selectedMonth) {
        const monthName = new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' }).replace(' ', '_');
        fileName += `_${monthName}`;
      } else if (exportType === 'year' && selectedYear) {
        fileName += `_${selectedYear}`;
      }
      fileName += `_${new Date().toISOString().split('T')[0]}.csv`;
      
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      alert(`✅ Report exported successfully!\n\n${recordsToExport.length} records exported for ${exportType === 'month' ? 'selected month' : exportType === 'year' ? 'selected year' : 'all time'}`);
    } catch (error) {
      console.error('❌ Export error:', error);
      throw error;
    }
  };

  const getStatusChip = (employee) => {
    let isActive = false;
    
    if (typeof employee.status === 'string') {
      isActive = employee.status.toLowerCase() === 'active';
    } else if (typeof employee.status === 'boolean') {
      isActive = employee.status;
    } else if (employee.is_active !== undefined) {
      isActive = employee.is_active;
    } else {
      isActive = true;
    }

    const isOnLeave = employee.on_leave_today || employee.hasFullDayLeave || false;
    
    const displayStatus = isOnLeave ? 'On Leave' : (isActive ? 'Active' : 'Inactive');
    const statusColor = isOnLeave ? '#FFA726' : (isActive ? '#4CAF50' : '#F44336');
    const bgColor = isOnLeave ? '#FFF3E0' : (isActive ? '#E8F5E9' : '#FFEBEE');

    return (
      <span 
        className="hr-emp-dash-status-badge"
        style={{
          backgroundColor: bgColor,
          color: statusColor,
          border: `1px solid ${statusColor}`,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600'
        }}
      >
        {displayStatus}
      </span>
    );
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = 
      employee.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      employee.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = 
      departmentFilter === 'All Departments' || 
      employee.department === departmentFilter ||
      employee.role === departmentFilter;
    
    const matchesStatus = (() => {
      if (statusFilter === 'All Status') return true;
      
      const isActive = typeof employee.status === 'string' 
        ? employee.status.toLowerCase() === 'active'
        : employee.status === true || employee.is_active === true;
      
      return statusFilter === 'Active' ? isActive : !isActive;
    })();
    
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const totalEmployees = filteredEmployees.length;
  const activeEmployees = filteredEmployees.filter(emp => {
    const status = emp.status;
    return typeof status === 'string' 
      ? status.toLowerCase() === 'active'
      : status === true || emp.is_active === true;
  }).length;
  const onLeaveToday = filteredEmployees.filter(emp => 
    emp.on_leave_today || emp.hasFullDayLeave
  ).length;
  
  const avgUtilization = filteredEmployees.length > 0
    ? Math.round(
        filteredEmployees.reduce((sum, emp) => {
          const totalUsed = (emp.casual_used || 0) + (emp.medical_used || 0);
          const totalAllocated = (emp.casual_allocated || 15) + (emp.medical_allocated || 15);
          return sum + (totalUsed / totalAllocated) * 100;
        }, 0) / filteredEmployees.length
      )
    : 0;

  const pendingRequests = filteredEmployees.reduce((sum, emp) => 
    sum + (emp.pending_leaves || 0), 0
  );

  const handleExport = () => {
    try {
      const headers = ['Name', 'Email', 'Department', 'Status', 'Team Head', 'Casual Leave', 'Medical Leave', 'Last Leave'];
      const csvContent = [
        headers.join(','),
        ...filteredEmployees.map(emp => {
          const isActive = typeof emp.status === 'string' 
            ? emp.status.toLowerCase() === 'active'
            : emp.status === true || emp.is_active === true;
          const isOnLeave = emp.on_leave_today || emp.hasFullDayLeave || false;
          const statusText = isOnLeave ? 'On Leave' : (isActive ? 'Active' : 'Inactive');

          return [
            `"${emp.name}"`,
            `"${emp.email}"`,
            `"${emp.department || emp.role}"`,
            `"${statusText}"`,
            `"${emp.manager_name || 'N/A'}"`,
            `"${emp.casual_used || 0}/${emp.casual_allocated || 15}"`,
            `"${emp.medical_used || 0}/${emp.medical_allocated || 15}"`,
            `"${formatLastLeaveDate(emp.last_leave_date)}"`
          ].join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert('Employee data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="hr-emp-dash-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hr-emp-dash-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#f44336'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="hr-emp-dash-container">
      <div className="hr-emp-dash-content">
        <div className="hr-emp-dash-title-section">
          <div className='hr-emp-dash-title-name'>
            <div className="hr-emp-dash-title">Employees</div>
          </div>
          <div className="hr-emp-dash-search-box">
            <span className="hr-emp-dash-search-top">
              <img src="/Images/Search.png" alt="Search" />
            </span>
            <input 
              type="text" 
              placeholder="Search for..." 
              className="hr-emp-dash-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="hr-emp-dash-stats-grid">
          <div className="hr-emp-dash-stat-card">
            <div className="hr-emp-dash-stat-header">
              <span className="hr-emp-dash-stat-label" style={{color:'#2196F3'}}>Total Employees</span>
              <span className="hr-emp-dash-stat-icon hr-emp-dash-icon-blue">
                <img src="/Images/Totalemp.png" alt="Total" />
              </span>
            </div>
            <div className="hr-emp-dash-stat-value">{totalEmployees}</div>
            <div className="hr-emp-dash-stat-footer" style={{color:'#21A345'}}>
              <img src="/Images/allactive.png" alt="Active" />
              {activeEmployees} Active
            </div>
          </div>

          <div className="hr-emp-dash-stat-card">
            <div className="hr-emp-dash-stat-header">
              <span className="hr-emp-dash-stat-label" style={{color:'#009688'}}>On Leave Today</span>
              <span className="hr-emp-dash-stat-icon hr-emp-dash-icon-teal">
                <img src="/Images/tabler_users.png" alt="Leave" />
              </span>
            </div>
            <div className="hr-emp-dash-stat-value">{onLeaveToday}</div>
            <div className="hr-emp-dash-stat-footer" style={{color:'#848484'}}>
              {onLeaveToday === 0 ? 'No leaves scheduled' : `${onLeaveToday} employee(s) on leave`}
            </div>
          </div>

          <div className="hr-emp-dash-stat-card">
            <div className="hr-emp-dash-stat-header">
              <span className="hr-emp-dash-stat-label" style={{color:'#FF0004'}}>Avg Utilization</span>
              <span className="hr-emp-dash-stat-icon hr-emp-dash-icon-red">
                <img src="/Images/solar-linear.png" alt="Utilization" />
              </span>
            </div>
            <div className="hr-emp-dash-stat-value">{avgUtilization}%</div>
            <div className="hr-emp-dash-stat-footer" style={{color:'#21A345'}}>Healthy range</div>
          </div>

          <div className="hr-emp-dash-stat-card">
            <div className="hr-emp-dash-stat-header">
              <span className="hr-emp-dash-stat-label" style={{color:'#FE7239'}}>Pending Requests</span>
              <span className="hr-emp-dash-stat-icon hr-emp-dash-icon-orange">
                <img src="/Images/clockorange.png" alt="Pending" />
              </span>
            </div>
            <div className="hr-emp-dash-stat-value">{pendingRequests}</div>
            <div className="hr-emp-dash-stat-footer" style={{color: pendingRequests > 0 ? '#FE7239' : '#21A345'}}>
              {pendingRequests > 0 ? 'Needs attention' : 'All clear'}
            </div>
          </div>
        </div>

        <div className="hr-emp-dash-filters">
          <div className="hr-emp-dash-filter-search">
            <span className="hr-emp-dash-search-icon" style={{color:'#2A2A2A'}}>
              <img src="/Images/Searchblack.png" alt="Search" />
            </span>
            <input 
              type="text" 
              placeholder="Search by name or email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div  className='hr-emp-dash-filter-search-two'>
            <select 
              className="hr-emp-dash-filter-select"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option>All Departments</option>
              <option>Engineering</option>
              <option>Marketing</option>
              <option>Sales</option>
              <option>HR</option>
              <option>Finance</option>
            </select>
            <select 
              className="hr-emp-dash-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option>All Status</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
            <button 
              className="hr-emp-dash-export-btn"
              onClick={handleExport}
              disabled={filteredEmployees.length === 0}
            >
              Export ↓
            </button>
          </div>
        </div>

        <div className="hr-emp-dash-employee-list">
          {filteredEmployees.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              fontSize: '16px'
            }}>
              {employees.length === 0 
                ? 'No employees found. Only users with Employee role are shown here.'
                : 'No employees match the current filters.'}
            </div>
          ) : (
            filteredEmployees.map((employee) => (
              <div key={employee.id} className="hr-emp-dash-employee-card">
                <div className="hr-emp-dash-employee-header">
                  <div className="hr-emp-dash-employee-info">
                    <div className="hr-emp-dash-employee-avatar">
                      <img 
                        src={employee.profile_picture || employee.avatar || `https://i.pravatar.cc/150?img=${employee.id}`} 
                        alt={employee.name}
                        onError={(e) => {
                          e.target.src = `https://i.pravatar.cc/150?img=${employee.id}`;
                        }}
                      />
                    </div>
                    <div>
                      <div className="hr-emp-dash-employee-name">{employee.name}</div>
                      <div className="hr-emp-dash-employee-dept">{employee.department || employee.role}</div>
                    </div>
                  </div>
                  {getStatusChip(employee)}
                </div>

                <div className="hr-emp-dash-leave-bars">
                  <div className="hr-emp-dash-leave-bar">
                    <div className="hr-emp-dash-leave-bar-header">
                      <span className="hr-emp-dash-leave-label">Casual Leave</span>
                      <span className="hr-emp-dash-leave-count">
                        {employee.casual_used || 0}/{employee.casual_allocated || 12}
                      </span>
                    </div>
                    <div className="hr-emp-dash-progress-bar">
                      <div 
                        className="hr-emp-dash-progress-fill hr-emp-dash-progress-blue"
                        style={{ 
                          width: `${Math.min(((employee.casual_used || 0) / (employee.casual_allocated || 12)) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="hr-emp-dash-leave-bar">
                    <div className="hr-emp-dash-leave-bar-header">
                      <span className="hr-emp-dash-leave-label">Medical Leave</span>
                      <span className="hr-emp-dash-leave-count">
                        {employee.medical_used || 0}/{employee.medical_allocated || 15}
                      </span>
                    </div>
                    <div className="hr-emp-dash-progress-bar">
                      <div 
                        className="hr-emp-dash-progress-fill hr-emp-dash-progress-red"
                        style={{ 
                          width: `${Math.min(((employee.medical_used || 0) / (employee.medical_allocated || 15)) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Carryforward Information */}
                <div className="hr-emp-dash-leave-bars" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee' }}>
                  <div className="hr-emp-dash-leave-bar">
                    <div className="hr-emp-dash-leave-bar-header">
                      <span className="hr-emp-dash-leave-label">Casual Carryforward</span>
                      <span className="hr-emp-dash-leave-count">
                        {employee.casual_carry_forward || 0} days
                      </span>
                    </div>
                    <div className="hr-emp-dash-progress-bar">
                      <div 
                        className="hr-emp-dash-progress-fill hr-emp-dash-progress-green"
                        style={{ 
                          width: `${Math.min(((employee.casual_carry_forward || 0) / 12) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="hr-emp-dash-employee-footer">
                  <div className="hr-emp-dash-footer-info">
                    <div>
                      <div className="hr-emp-dash-footer-label">Utilization</div>
                      <div className="hr-emp-dash-footer-value" style={{color:'#2196F3'}}>
                        {Math.round(
                          ((employee.casual_used || 0) + (employee.medical_used || 0)) / 
                          ((employee.casual_allocated || 15) + (employee.medical_allocated || 15)) * 100
                        )}%
                      </div>
                    </div>
                    <div className='hr-emp-dash-footer-info-two' style={{paddingLeft:'13cm'}}>
                      <div className="hr-emp-dash-footer-label">Last Leave</div>
                      <div className="hr-emp-dash-footer-value" title={employee.last_leave_date}>
                        {formatLastLeaveDate(employee.last_leave_date)}
                      </div>
                    </div>
                  </div>
                  <div className="hr-emp-dash-footer-actions">
                    <button 
                      className="hr-emp-dash-btn-secondary"
                      onClick={() => handleViewDetails(employee)}
                    >
                      👁 View Details
                    </button>
                    <button 
                      className="hr-emp-dash-btn-primary"
                      onClick={() => handleAdjust(employee)}
                    >
                      <img src="/Images/Adjust.png" alt="Adjust" />Adjust
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showEmployeeDetails && selectedEmployee && (
        <div className="hr-emp-dash-modal-overlay" onClick={() => setShowEmployeeDetails(false)}>
          <div className="hr-emp-dash-modal hr-emp-dash-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="hr-emp-dash-modal-header">
              <h2 className="hr-emp-dash-modal-title">Employee Details</h2>
              <button className="hr-emp-dash-modal-close" onClick={() => setShowEmployeeDetails(false)}>✕</button>
            </div>
            
            <div className="hr-emp-dash-modal-content">
              <div className="hr-emp-dash-employee-profile">
                <div className="hr-emp-dash-profile-avatar">
                  <img 
                    src={selectedEmployee.profile_picture || selectedEmployee.avatar || `https://i.pravatar.cc/150?img=${selectedEmployee.id}`} 
                    alt={selectedEmployee.name}
                    onError={(e) => {
                      e.target.src = `https://i.pravatar.cc/150?img=${selectedEmployee.id}`;
                    }}
                  />
                </div>
                <div>
                  <h3 className="hr-emp-dash-profile-name">{selectedEmployee.name}</h3>
                  <p className="hr-emp-dash-profile-role">{selectedEmployee.role}</p>
                </div>
                {getStatusChip(selectedEmployee)}
              </div>

              <div className="hr-emp-dash-detail-grid">
                <div className="hr-emp-dash-detail-item">
                  <span className="hr-emp-dash-detail-icon">
                    <img src="/Images/Mail.png" alt="Email" />
                  </span>
                  <span>{selectedEmployee.email}</span>
                </div>
                <div className="hr-emp-dash-detail-item">
                  <span className="hr-emp-dash-detail-icon">
                    <img src="/Images/Phone.png" alt="Phone" />
                  </span>
                  <span>{selectedEmployee.ContactNumber1 || 'N/A'}</span>
                </div>
                <div className="hr-emp-dash-detail-item">
                  <span className="hr-emp-dash-detail-icon">
                    <img src="/Images/Location.png" alt="Location" />
                  </span>
                  <span>{selectedEmployee.Address || 'N/A'}</span>
                </div>
                <div className="hr-emp-dash-detail-item">
                  <span className="hr-emp-dash-detail-icon">
                    <img src="/Images/Role.png" alt="Role" />
                  </span>
                  <span>{selectedEmployee.department || selectedEmployee.role}</span>
                </div>
                <div className="hr-emp-dash-detail-item">
                  <span className="hr-emp-dash-detail-icon">
                    <img src="/Images/member.png" alt="Manager" />
                  </span>
                  <span>Manager: {selectedEmployee.manager_name || 'N/A'}</span>
                </div>
                <div className="hr-emp-dash-detail-item">
                  <span className="hr-emp-dash-detail-icon">
                    <img src="/Images/calender.png" alt="Joined" />
                  </span>
                  <span>Joined: {selectedEmployee.joinDate || selectedEmployee.member_since || 'N/A'}</span>
                </div>
              </div>

              <h3 className="hr-emp-dash-section-title">Leave Balances</h3>
              <div className="hr-emp-dash-balance-cards">
                <div className="hr-emp-dash-balance-card hr-emp-dash-balance-blue">
                  <div className="hr-emp-dash-balance-label" style={{color:'#1874C9'}}>Casual Leave</div>
                  <div className="hr-emp-dash-balance-value" style={{color:'#1874C9'}}>
                    {selectedEmployee.casual_used || 0}
                  </div>
                  <div className="hr-emp-dash-balance-total" style={{color:'#1874C9'}}>
                    of {selectedEmployee.casual_allocated || 12} days available
                  </div>
                  <div className="hr-emp-dash-balance-progress">
                    <div 
                      className="hr-emp-dash-balance-progress-fill"
                      style={{ 
                        width: `${Math.min(((selectedEmployee.casual_used || 0) / (selectedEmployee.casual_allocated || 12)) * 100, 100)}%`,
                        backgroundColor: '#1874C9'
                      }}
                    ></div>
                  </div>
                </div>

                <div className="hr-emp-dash-balance-card hr-emp-dash-balance-red">
                  <div className="hr-emp-dash-balance-label" style={{color:'#FF0004'}}>Medical Leave</div>
                  <div className="hr-emp-dash-balance-value" style={{color:'#FF0004'}}>
                    {selectedEmployee.medical_used || 0}
                  </div>
                  <div className="hr-emp-dash-balance-total" style={{color:'#FF0004'}}>
                    of {selectedEmployee.medical_allocated || 15} days available
                  </div>
                  <div className="hr-emp-dash-balance-progress">
                    <div 
                      className="hr-emp-dash-balance-progress-fill"
                      style={{ 
                        width: `${Math.min(((selectedEmployee.medical_used || 0) / (selectedEmployee.medical_allocated || 15)) * 100, 100)}%`,
                        backgroundColor: '#FF0004'
                      }}
                    ></div>
                  </div>
                </div>

                <div className="hr-emp-dash-balance-card hr-emp-dash-balance-yellow">
                  <div className="hr-emp-dash-balance-label" style={{color:'#A78602'}}>Permission</div>
                  <div className="hr-emp-dash-balance-value" style={{color:'#A78602'}}>
                    {selectedEmployee.permission_used || 0}
                  </div>
                  <div className="hr-emp-dash-balance-total" style={{color:'#A78602'}}>
                    of {selectedEmployee.permission_allocated || 2} hours available
                  </div>
                  <div className="hr-emp-dash-balance-progress">
                    <div 
                      className="hr-emp-dash-balance-progress-fill"
                      style={{ 
                        width: `${Math.min(((selectedEmployee.permission_used || 0) / (selectedEmployee.permission_allocated || 2)) * 100, 100)}%`,
                        backgroundColor: '#A78602'
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Carryforward Balance Card */}
              <div className="hr-emp-dash-balance-cards">
                <div className="hr-emp-dash-balance-card hr-emp-dash-balance-green">
                  <div className="hr-emp-dash-balance-label" style={{color:'#2E7D32'}}>Casual Carryforward</div>
                  <div className="hr-emp-dash-balance-value" style={{color:'#2E7D32'}}>
                    {selectedEmployee.casual_carry_forward || 0}
                  </div>
                  <div className="hr-emp-dash-balance-total" style={{color:'#2E7D32'}}>
                    days carried forward
                  </div>
                  <div className="hr-emp-dash-balance-progress">
                    <div 
                      className="hr-emp-dash-balance-progress-fill"
                      style={{ 
                        width: `${Math.min(((selectedEmployee.casual_carry_forward || 0) / 12) * 100, 100)}%`,
                        backgroundColor: '#2E7D32'
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="hr-emp-dash-activity-header">
                <h3 className="hr-emp-dash-section-title">Recent Leave Activity</h3>
                <button 
                  className="hr-emp-dash-view-all"
                  onClick={() => {
                    setShowEmployeeDetails(false);
                    setShowFullHistory(true);
                  }}
                >
                  View All History
                </button>
              </div>

              <div className="hr-emp-dash-activity-list">
                <div className="hr-emp-dash-activity-item hr-emp-dash-activity-casual">
                  <div>
                    <div className="hr-emp-dash-activity-type">Last Approved Leave</div>
                    <div className="hr-emp-dash-activity-date">
                      {formatLastLeaveDate(selectedEmployee.last_leave_date)}
                    </div>
                  </div>
                  <span className="hr-emp-dash-activity-status">
                    <img src="/Images/Approved.png" alt="Status" />
                    {selectedEmployee.approved_leaves || 0} Total Approved
                  </span>
                </div>
              </div>

              <div className="hr-emp-dash-modal-actions">
                <button className="hr-emp-dash-btn-primary-pop" onClick={() => {
                  setShowEmployeeDetails(false);
                  setShowAdjustBalance(true);
                }}>
                  ✏ Adjust Balance
                </button>
                <button 
                  className="hr-emp-dash-btn-outline"
                  onClick={() => {
                    setShowEmployeeDetails(false);
                    setShowFullHistory(true);
                  }}
                >
                  <img src="/Images/Group.png" alt="History" /> View Full History
                </button>
                <button 
                  className="hr-emp-dash-btn-outline"
                  onClick={() => setShowExportOptions(true)}
                >
                  <img src="/Images/Group.png" alt="Export" /> Export Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAdjustBalance && selectedEmployee && (
        <AdjustBalanceModal
          employee={selectedEmployee}
          onClose={() => setShowAdjustBalance(false)}
          onSubmit={handleAdjustmentSubmit}
        />
      )}

      {showFullHistory && selectedEmployee && (
        <FullHistoryModal
          employee={selectedEmployee}
          onClose={() => setShowFullHistory(false)}
        />
      )}

      {showExportOptions && selectedEmployee && (
        <ExportOptionsModal
          employee={selectedEmployee}
          onClose={() => setShowExportOptions(false)}
          onExport={handleExportWithOptions}
        />
      )}
    </div>
  );
};

export default HREmployeeDashboard;